import {useLazyQuery} from "@apollo/client";
import React from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {CheckLimitV1Response, GQL_CHECK_LIMIT_V1} from "@/lib/queries/billing/legacyV1.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {PopupContent} from "@/pages/billing/v1/instantCheckout/PopupContent.tsx";
import {LimitCheckProps} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";

interface LimitCheckServiceLimitProps extends Omit<LimitCheckProps, "serviceType"> {
  suppressUnavailable?: boolean;
  serviceType: MonitoringServiceType;
}

// IMPORTANT: original InstantCheckout.tsx requires more updates to support other check types
const SUPPORTED_SERVICES = [MonitoringServiceType.CLOUDSTATUS];

export const LimitCheckServiceLimit = React.forwardRef(
  ({serviceType, suppressUnavailable}: LimitCheckServiceLimitProps, ref) => {
    const {user} = useCurrentUserContext();
    const [showInstantCheckout, setShowInstantCheckout] = React.useState(false);
    const [checkLimits, {data}] = useLazyQuery<CheckLimitV1Response>(GQL_CHECK_LIMIT_V1, {
      fetchPolicy: "no-cache",
      nextFetchPolicy: "no-cache",
    });

    const checkIfLimitHit = async (props?: LimitCheckProps): Promise<boolean> => {
      const monitoringServiceType = props?.serviceType || serviceType;
      if (!monitoringServiceType || !user || user.account.subscription_type !== AccountSubscriptionType.BILLING_1_0) {
        return false;
      }
      if (!SUPPORTED_SERVICES.includes(monitoringServiceType as MonitoringServiceType)) {
        return false; // unsupported service type
      }

      try {
        const result = await checkLimits({
          variables: {
            service: monitoringServiceType,
            requested: props?.requestedValue,
          },
        });
        if (result.data?.checkSubscriptionV1Limit?.is_exhausted === true) {
          if (result.data.checkSubscriptionV1Limit.current_limit === 0 && suppressUnavailable) {
            return false; // Feature is unavailable, but we don't want to force it
          }
          setShowInstantCheckout(true);
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error("Error checking service limit:", error);
        return false;
      }
    };

    React.useImperativeHandle(ref, () => ({
      checkIfLimitHit,
    }));

    const show = showInstantCheckout;
    const response = data?.checkSubscriptionV1Limit;
    if (!response?.current_plan) {
      return null;
    }

    const nextPlan = response.next_plan;
    return (
      <PopupContent
        show={show}
        currentPlan={response.current_plan}
        limitName={response.limit_name}
        nextPlan={nextPlan}
        service={serviceType}
        onClose={() => setShowInstantCheckout(false)}
        requestedAt={response.pending_purchase_request_at}
      />
    );
  }
);
